import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RotateCcw, Sparkles, AlertTriangle, AlertCircle, Info,
  CheckCircle2, ThumbsUp, ThumbsDown, XCircle, ArrowRight, Zap,
  MessageSquare,
} from "lucide-react";

const TYPES = [
  { id: "deal_summary",     label: "Deal Summary" },
  { id: "exit_strategy",    label: "Exit Strategy" },
  { id: "offer_guidance",   label: "Offer Guidance" },
  { id: "risk_alert",       label: "Risk Alert" },
  { id: "missing_data",     label: "Missing Data" },
  { id: "next_best_action", label: "Next Best Action" },
];

const SEV_COLOR = {
  info:     { pill: "bg-slate-100 text-slate-700 border-slate-200",  Icon: Info },
  low:      { pill: "bg-slate-100 text-slate-700 border-slate-200",  Icon: Info },
  medium:   { pill: "bg-amber-100 text-amber-800 border-amber-200",  Icon: AlertTriangle },
  high:     { pill: "bg-orange-100 text-orange-800 border-orange-200", Icon: AlertTriangle },
  critical: { pill: "bg-red-100    text-red-800    border-red-300",    Icon: AlertCircle },
};

const STATUS_COLOR = {
  complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
  partial:  "bg-amber-100   text-amber-800   border-amber-300",
  blocked:  "bg-red-100     text-red-800     border-red-300",
  failed:   "bg-slate-100   text-slate-700   border-slate-300",
  stale:    "bg-slate-100   text-slate-700   border-slate-300",
};

const PRIORITY_COLOR = {
  critical: "bg-red-100 text-red-800 border-red-300",
  high:     "bg-orange-100 text-orange-800 border-orange-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-200",
  low:      "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AIRecommendationsTab({ property }) {
  const [byType, setByType] = useState({});
  const [actions, setActions] = useState([]);
  const [activeType, setActiveType] = useState("deal_summary");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cur }, { data: acts }] = await Promise.all([
        api.get(`/v3/properties/${property.id}/ai-recommendations/current`),
        api.get(`/v3/properties/${property.id}/ai-recommendations/actions`),
      ]);
      setByType(cur.by_type || {});
      setActions(acts.items || []);
    } catch (err) {
      toast.error("Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  }, [property.id]);

  useEffect(() => { load(); }, [load]);

  const generate = useCallback(async (force = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/ai-recommendations/generate`,
        { type: activeType, force_refresh: force },
      );
      setByType((s) => ({ ...s, [activeType]: data }));
      toast.success(`Recommendation ready — ${data.status}`);
      // Refresh actions list
      const { data: acts } = await api.get(
        `/v3/properties/${property.id}/ai-recommendations/actions`,
      );
      setActions(acts.items || []);
    } catch (err) {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  }, [property.id, activeType]);

  const submitFeedback = useCallback(async (rec_id, kind) => {
    try {
      await api.post(`/v3/ai-recommendations/${rec_id}/feedback`, { kind });
      toast.success("Feedback recorded");
      load();
    } catch (err) {
      toast.error("Feedback failed");
    }
  }, [load]);

  const convertAction = useCallback(async (rec_id, action_id) => {
    try {
      const { data } = await api.post(
        `/v3/ai-recommendations/${rec_id}/actions/${action_id}/convert-task`);
      const taskTitle = data?.task?.title || "Task";
      toast.success(`Created task: “${taskTitle}”`, {
        description: "Track it in the Tasks tab.",
      });
      const { data: acts } = await api.get(
        `/v3/properties/${property.id}/ai-recommendations/actions`,
      );
      setActions(acts.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Convert failed");
    }
  }, [property.id]);

  const current = byType[activeType];

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="ai-rec-loading">
        <Loader2 size={14} className="animate-spin"/> Loading recommendations…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tab-panel-ai_recommendations">
      {/* Type selector */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Type:</span>
          {TYPES.map((t) => (
            <button key={t.id}
                    onClick={() => setActiveType(t.id)}
                    data-testid={`ai-rec-type-${t.id}`}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                      activeType === t.id
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}>
              {t.label}
              {byType[t.id] && (
                <span className={`ml-1 inline-block w-1.5 h-1.5 rounded-full ${
                  byType[t.id].status === "complete" ? "bg-emerald-500" :
                  byType[t.id].status === "partial"  ? "bg-amber-500"   :
                  byType[t.id].status === "blocked"  ? "bg-red-500"     : "bg-slate-400"
                }`}/>
              )}
            </button>
          ))}
          <button onClick={() => generate(true)} disabled={generating}
                  data-testid="ai-rec-generate-btn"
                  className="ml-auto inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {generating ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            {current ? "Refresh" : "Generate"}
          </button>
        </div>
      </div>

      {current ? (
        <RecommendationCard rec={current}
                             onFeedback={submitFeedback}
                             onConvertAction={convertAction}
                             actions={actions}/>
      ) : (
        <EmptyState onGenerate={() => generate(false)} generating={generating}/>
      )}

      <p className="text-[11px] text-slate-500 italic px-1" data-testid="ai-rec-disclaimer">
        Advisory guidance — verify before acting. Deterministic engine outputs remain authoritative.
      </p>
    </div>
  );
}

function EmptyState({ onGenerate, generating }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="ai-rec-empty">
      <Sparkles size={24} className="mx-auto text-slate-400 mb-2"/>
      <div className="text-sm font-medium text-slate-800">No recommendation yet</div>
      <div className="text-xs text-slate-600 mt-1">Click Generate to run the advisory engine.</div>
      <button onClick={onGenerate} disabled={generating}
              className="mt-3 inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
        {generating ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
        Generate
      </button>
    </div>
  );
}

function RecommendationCard({ rec, onFeedback, onConvertAction, actions }) {
  const conf = Math.round((rec.confidence || 0) * 100);
  const relatedActions = actions.filter((a) => a.recommendation_id === rec.id);
  return (
    <div className="space-y-4">
      {/* Hero */}
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="ai-rec-hero">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-amber-800/80">
              {(rec.recommendation_type || "").replace(/_/g, " ")}
            </div>
            <div className="text-xl font-bold text-slate-900 mt-1" data-testid="ai-rec-headline">
              {rec.headline || "—"}
            </div>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed" data-testid="ai-rec-summary">
              {rec.summary || "—"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${STATUS_COLOR[rec.status] || STATUS_COLOR.complete}`}
                  data-testid="ai-rec-status">
              {rec.status}
            </span>
            {rec.is_stale && (
              <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border bg-amber-100 text-amber-800 border-amber-300"
                    data-testid="ai-rec-stale-badge"
                    title="Upstream data changed since this was generated">
                ⟳ Stale — refresh
              </span>
            )}
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Confidence</div>
              <div className={`text-2xl font-bold tabular-nums ${
                conf >= 70 ? "text-emerald-700" :
                conf >= 40 ? "text-amber-700"   : "text-red-700"}`}
                   data-testid="ai-rec-confidence">
                {conf}%
              </div>
            </div>
          </div>
        </div>
        {/* Primary + Secondary */}
        {(rec.primary_recommendation?.strategy || rec.secondary_recommendation?.strategy) && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {rec.primary_recommendation?.strategy && (
              <div className="rounded-lg border border-emerald-200 bg-white p-3">
                <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold">Primary</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5" data-testid="ai-rec-primary">
                  {rec.primary_recommendation.strategy.replace(/_/g, " ").toUpperCase()}
                </div>
                <div className="text-[11px] text-slate-600 mt-1">{rec.primary_recommendation.reason}</div>
              </div>
            )}
            {rec.secondary_recommendation?.strategy && (
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Alternate</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">
                  {String(rec.secondary_recommendation.strategy).replace(/_/g, " ").toUpperCase()}
                </div>
                <div className="text-[11px] text-slate-600 mt-1">{rec.secondary_recommendation.reason}</div>
              </div>
            )}
          </div>
        )}
        {rec.fallback_reason && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-[11px] text-red-900" data-testid="ai-rec-failure">
            <b>AI narrative unavailable</b> — {rec.fallback_reason}. Deterministic
            engine outputs above are complete and safe. Click <b>Refresh</b> to
            retry the LLM call.
          </div>
        )}
      </div>

      {/* Two-column: Strengths | Risks */}
      <div className="grid gap-4 md:grid-cols-2">
        <StrengthsPanel strengths={rec.strengths}/>
        <RisksPanel risks={rec.risks} narrative={rec.risk_narrative}/>
      </div>

      {/* Offer guidance */}
      {rec.offer_guidance && <OfferGuidancePanel guidance={rec.offer_guidance}/>}

      {/* Missing Data */}
      {rec.missing_data?.length > 0 && (
        <MissingDataPanel items={rec.missing_data} narrative={rec.missing_data_narrative}/>
      )}

      {/* Next Actions */}
      {relatedActions.length > 0 && (
        <NextActionsPanel actions={relatedActions}
                           onConvert={(action_id) => onConvertAction(rec.id, action_id)}/>
      )}

      {/* Negotiation talking points */}
      {rec.negotiation_talking_points?.length > 0 && (
        <TalkingPointsPanel points={rec.negotiation_talking_points}/>
      )}

      {/* Feedback */}
      <FeedbackBar rec={rec} onFeedback={onFeedback}/>
    </div>
  );
}

function StrengthsPanel({ strengths = [] }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-white p-4" data-testid="ai-rec-strengths">
      <div className="text-[10px] uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
        <CheckCircle2 size={12}/> Strengths
      </div>
      {strengths.length === 0 ? (
        <div className="text-xs text-slate-500 italic">None called out.</div>
      ) : (
        <ul className="space-y-2">
          {strengths.map((s, i) => (
            <li key={i} className="text-xs text-slate-800">
              <div className="font-semibold">{s.code}</div>
              <div>{s.text}</div>
              {s.evidence?.length > 0 && (
                <div className="text-[10px] text-slate-500 mt-0.5">→ {s.evidence.join(", ")}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RisksPanel({ risks = [], narrative = {} }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-rec-risks">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
        <AlertTriangle size={12}/> Risks
      </div>
      {risks.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No risks detected.</div>
      ) : (
        <ul className="space-y-2">
          {risks.map((r, i) => {
            const sev = SEV_COLOR[r.severity] || SEV_COLOR.medium;
            const Ic = sev.Icon;
            return (
              <li key={i} className={`rounded-md border px-2 py-1.5 ${sev.pill}`}
                  data-testid={`ai-rec-risk-${r.code}`}>
                <div className="flex items-center gap-1 text-[11px] font-semibold uppercase">
                  <Ic size={11}/> {r.severity}
                  <span className="ml-1 opacity-70">{r.code}</span>
                </div>
                <div className="text-xs mt-0.5">{r.text}</div>
                {narrative[r.code] && (
                  <div className="text-[10px] italic opacity-80 mt-0.5">{narrative[r.code]}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function OfferGuidancePanel({ guidance }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-rec-offer-guidance">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
        <Zap size={12}/> Offer Guidance
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <Metric label="Opening" value={`$${fmt(guidance.opening)}`} color="text-slate-600"/>
        <Metric label="Target"  value={`$${fmt(guidance.target)}`}  color="text-emerald-700"/>
        <Metric label="Walk-away" value={`$${fmt(guidance.walk_away)}`} color="text-red-700"/>
      </div>
      <div className="text-[10px] italic text-slate-500 mt-2">Deterministic — computed from wholesale scenario.</div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-2">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-bold tabular-nums mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function MissingDataPanel({ items = [], narrative = "" }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="ai-rec-missing">
      <div className="text-[10px] uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1">
        <Info size={12}/> Missing Information
      </div>
      {narrative && <p className="text-xs text-slate-700 mb-2">{narrative}</p>}
      <ul className="space-y-1">
        {items.map((m, i) => (
          <li key={i} className="text-xs text-slate-800 flex items-start gap-2">
            <span className="text-[9px] uppercase font-semibold px-1 py-0.5 rounded bg-white border border-amber-200 text-amber-800 flex-shrink-0 mt-0.5">
              {m.impact}
            </span>
            <span>
              <b>{m.field}</b>
              {m.blocks?.length > 0 && (
                <span className="ml-1 text-[10px] text-slate-500">blocks: {m.blocks.join(", ")}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NextActionsPanel({ actions = [], onConvert }) {
  const ordered = [...actions].sort((a, b) => rank(b.priority) - rank(a.priority));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-rec-actions">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
        <ArrowRight size={12}/> Next Best Actions
      </div>
      <ul className="space-y-2">
        {ordered.map((a, i) => (
          <li key={a.id || i} className="flex items-start gap-2 text-xs" data-testid={`ai-rec-action-${a.action}`}>
            <span className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${PRIORITY_COLOR[a.priority] || PRIORITY_COLOR.medium}`}>
              {a.priority}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-slate-900 font-medium">{a.text}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Owner: <b>{a.owner_role?.replace(/_/g, " ")}</b>
                {a.evidence?.length > 0 && ` · Refs: ${a.evidence.join(", ")}`}
              </div>
            </div>
            {a.status === "converted" ? (
              <span
                data-testid={`ai-rec-converted-${a.id}`}
                className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold"
                title={a.task_id ? `Task ID ${a.task_id}` : "Converted"}
              >
                <CheckCircle2 size={11}/> Task created
              </span>
            ) : (
              <button onClick={() => onConvert(a.id)}
                      data-testid={`ai-rec-convert-${a.id}`}
                      className="text-[10px] font-semibold text-amber-700 hover:text-amber-900
                                 border border-amber-300 hover:border-amber-500
                                 rounded px-2 py-0.5 flex items-center gap-1 whitespace-nowrap">
                <ArrowRight size={10}/> Convert to Task
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TalkingPointsPanel({ points = [] }) {
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4" data-testid="ai-rec-talking-points">
      <div className="text-[10px] uppercase tracking-wider text-purple-700 mb-2 flex items-center gap-1">
        <MessageSquare size={12}/> Negotiation Talking Points
      </div>
      <ul className="list-disc list-inside space-y-1 text-xs text-slate-800">
        {points.map((p, i) => <li key={i}>{p}</li>)}
      </ul>
    </div>
  );
}

function FeedbackBar({ rec, onFeedback }) {
  const fb = rec.user_feedback;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-wrap items-center gap-2" data-testid="ai-rec-feedback">
      <span className="text-[11px] text-slate-600 mr-1">Was this helpful?</span>
      <button onClick={() => onFeedback(rec.id, "helpful")}
              data-testid="ai-rec-fb-helpful"
              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                fb?.kind === "helpful"
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"}`}>
        <ThumbsUp size={11}/> Helpful
      </button>
      <button onClick={() => onFeedback(rec.id, "not_helpful")}
              data-testid="ai-rec-fb-not-helpful"
              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                fb?.kind === "not_helpful"
                  ? "bg-amber-500 border-amber-500 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-amber-300"}`}>
        <ThumbsDown size={11}/> Not helpful
      </button>
      <button onClick={() => onFeedback(rec.id, "incorrect")}
              data-testid="ai-rec-fb-incorrect"
              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                fb?.kind === "incorrect"
                  ? "bg-red-600 border-red-600 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-red-300"}`}>
        <XCircle size={11}/> Incorrect
      </button>
      <span className="ml-auto text-[10px] text-slate-500">
        Model {rec.model_name} · {rec.prompt_version} · hash {rec.source_data_hash?.slice(0,8)}
      </span>
    </div>
  );
}

function fmt(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function rank(p) {
  return { critical: 4, high: 3, medium: 2, low: 1 }[p] || 0;
}
